/**
 * AppLauncher - Full-screen app launcher con búsqueda, categorías y drag-and-drop
 * Adaptado de NewLnF/components/AppLauncher.tsx para AdminConsole con MUI
 */

import React, { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Box,
  Typography,
  InputAdornment,
  Button,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useAppRegistryStore } from "../../stores/appRegistryStore";
import "./AppLauncher.css";

const AppLauncher = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const { apps, categories, settings, pinApp, unpinApp, accessApp, reorderApps } =
    useAppRegistryStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [draggedApp, setDraggedApp] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Filtrar apps
  const filteredApps = useMemo(() => {
    let appList = [...apps].filter((app) => !app.hidden).sort((a, b) => (a.order || 0) - (b.order || 0));

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      appList = appList.filter(
        (app) =>
          app.name.toLowerCase().includes(query) ||
          (app.description && app.description.toLowerCase().includes(query))
      );
    }

    // Filtrar por categoría
    if (selectedCategory) {
      appList = appList.filter((app) => app.categoryId === selectedCategory);
    }

    return appList;
  }, [apps, searchQuery, selectedCategory]);

  const pinnedApps = apps.filter((app) => app.pinned && !app.hidden).slice(0, 100);
  const recentApps = apps
    .filter((app) => app.lastAccessed && !app.hidden)
    .sort((a, b) => (b.lastAccessed?.getTime() || 0) - (a.lastAccessed?.getTime() || 0))
    .slice(0, 6);

  const getCategoryColor = (app) => {
    // Verificar color específico de app
    if (settings.appColors && settings.appColors[app.id]) {
      return settings.appColors[app.id];
    }

    // Verificar color de categoría
    const category = categories.find((cat) => cat.id === app.categoryId);
    if (category && settings.categoryColors && settings.categoryColors[category.id]) {
      return settings.categoryColors[category.id];
    }

    // Usar color por defecto de categoría
    return category?.color || app.color || "#757575";
  };

  const handleAppClick = (app) => {
    accessApp(app.id);
    if (app.path) {
      history.push(app.path);
    }
    onClose();
  };

  const handleTogglePin = (app, event) => {
    event.stopPropagation();
    if (app.pinned) {
      unpinApp(app.id);
    } else {
      const currentPinnedCount = apps.filter((a) => a.pinned).length;
      if (currentPinnedCount >= 100) {
        alert("Máximo de 100 apps pueden estar fijadas en la barra superior.");
        return;
      }
      pinApp(app.id);
    }
  };

  const handleDragStart = useCallback((e, app) => {
    setDraggedApp(app.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", app.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedApp(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e, targetIndex) => {
      e.preventDefault();
      const draggedAppId = e.dataTransfer.getData("text/plain");

      if (!draggedAppId || draggedAppId === filteredApps[targetIndex]?.id) {
        return;
      }

      const newAppIds = [...filteredApps.map((app) => app.id)];
      const draggedIndex = newAppIds.indexOf(draggedAppId);

      if (draggedIndex !== -1) {
        newAppIds.splice(draggedIndex, 1);
        newAppIds.splice(targetIndex, 0, draggedAppId);
        reorderApps(newAppIds);
      }

      setDraggedApp(null);
      setDragOverIndex(null);
    },
    [filteredApps, reorderApps]
  );

  const renderAppCard = (app, index, isDraggable = true) => {
    const categoryColor = getCategoryColor(app);
    const Icon = app.icon;
    const isDragging = draggedApp === app.id;
    const isDropTarget = dragOverIndex === index;

    return (
      <Card
        key={app.id}
        className={`app-card ${isDragging ? "dragging" : ""} ${isDropTarget ? "drop-target" : ""}`}
        draggable={isDraggable}
        onDragStart={isDraggable ? (e) => handleDragStart(e, app) : undefined}
        onDragEnd={isDraggable ? handleDragEnd : undefined}
        onDragOver={isDraggable && typeof index === "number" ? (e) => handleDragOver(e, index) : undefined}
        onDragLeave={isDraggable ? handleDragLeave : undefined}
        onDrop={isDraggable && typeof index === "number" ? (e) => handleDrop(e, index) : undefined}
        onClick={() => handleAppClick(app)}
        sx={{
          cursor: "pointer",
          position: "relative",
          transition: "all 0.2s",
          display: "flex",
          flexDirection: "column",
          height: 168,
          "&:hover": {
            boxShadow: 4,
          },
          opacity: isDragging ? 0.5 : 1,
          transform: isDragging ? "scale(0.95)" : "scale(1)",
          border: isDropTarget ? `2px solid ${categoryColor}` : "1px solid rgba(0,0,0,0.12)",
        }}
      >
        {isDraggable && (
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 4,
              left: 4,
              opacity: 0,
              transition: "opacity 0.2s",
              ".app-card:hover &": {
                opacity: 1,
              },
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        )}

        <IconButton
          size="small"
          onClick={(e) => handleTogglePin(app, e)}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            backgroundColor: app.pinned ? "#ffc107" : "rgba(0,0,0,0.04)",
            color: app.pinned ? "#fff" : "rgba(0,0,0,0.54)",
            "&:hover": {
              backgroundColor: app.pinned ? "#ffa000" : "#fff3cd",
              color: app.pinned ? "#fff" : "#ffc107",
            },
          }}
          title={app.pinned ? "Desfijar de la barra superior" : "Fijar en la barra superior"}
        >
          {app.pinned ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>

        <CardContent sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              backgroundColor: categoryColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              mb: 2,
            }}
          >
            {Icon && <Icon sx={{ fontSize: 24 }} />}
          </Box>
          <Typography variant="subtitle1" fontWeight="medium" noWrap>
            {app.name}
          </Typography>
          {app.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {app.description}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCategorySection = (categoryId, appList) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category || appList.length === 0) return null;

    const categoryColor = (settings.categoryColors && settings.categoryColors[categoryId]) || category.color;

    return (
      <Box key={categoryId} sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, pb: 1, borderBottom: `2px solid ${categoryColor}` }}>
          <Typography variant="h6" sx={{ color: categoryColor, fontWeight: 600 }}>
            {category.name}
          </Typography>
          <Chip label={appList.length} size="small" sx={{ ml: 1 }} />
        </Box>
        <Grid container spacing={2}>
          {appList.map((app, index) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={app.id}>
              {renderAppCard(app, index, true)}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: "90vh" } }}>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h5" fontWeight="bold">
            {t('appLauncher.title')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Barra de búsqueda */}
        <TextField
          fullWidth
          placeholder={t('search.placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Filtros de categoría */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
          <Chip
            label={t('appLauncher.all')}
            onClick={() => setSelectedCategory(null)}
            color={selectedCategory === null ? "primary" : "default"}
            clickable
          />
          {categories.map((category) => {
            const isSelected = selectedCategory === category.id;
            const categoryColor = (settings.categoryColors && settings.categoryColors[category.id]) || category.color;
            return (
              <Chip
                key={category.id}
                label={category.name}
                onClick={() => setSelectedCategory(category.id)}
                sx={{
                  backgroundColor: isSelected ? categoryColor : undefined,
                  color: isSelected ? "#fff" : undefined,
                  borderColor: categoryColor,
                  "&:hover": {
                    backgroundColor: isSelected ? categoryColor : `${categoryColor}20`,
                  },
                }}
                clickable
              />
            );
          })}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        {!searchQuery && !selectedCategory && (
          <>
            {/* Sección de apps fijadas */}
            {pinnedApps.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <StarIcon sx={{ color: "#ffc107" }} />
                  <Typography variant="h6" fontWeight={600}>
                      {t('appLauncher.pinned')}
                  </Typography>
                </Box>
                <Chip label={`${pinnedApps.length}/7 fijadas`} size="small" />
              </Box>
                <Grid container spacing={2}>
                  {pinnedApps.map((app) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={app.id}>
                      {renderAppCard(app, undefined, false)}
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ mt: 3, mb: 3 }} />
              </Box>
            )}

            {/* Sección de recientes */}
            {recentApps.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  {t('appLauncher.recent')}
                </Typography>
                <Grid container spacing={2}>
                  {recentApps.map((app) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={app.id}>
                      {renderAppCard(app, undefined, false)}
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ mt: 3, mb: 3 }} />
              </Box>
            )}
          </>
        )}

        {/* Apps por categoría */}
        {categories.map((category) => {
          const categoryApps = filteredApps.filter((app) => app.categoryId === category.id);
          return renderCategorySection(category.id, categoryApps);
        })}

        {/* Sin resultados */}
        {filteredApps.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography color="text.secondary">{t('appLauncher.noResults')}</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AppLauncher;

